import { styled } from "../../../styles";

/**
 * Height of the environment bar. Exported because the fixed sidebar has to
 * stop above it — one constant so the two can't drift apart.
 */
export const ENVIRONMENT_BANNER_HEIGHT = "56px";

/**
 * Viewport-height shell for the /admin section: the scrolling content area
 * and the environment bar are its two rows.
 *
 * The bar used to be `position: fixed` over the content, with 56px of bottom
 * padding on the scroll area meant to keep clear of it. That only worked at
 * the very end of a page — Chrome drops a scroll container's bottom padding
 * as soon as a descendant overflows it, and anywhere mid-scroll the bar
 * simply covered whatever was under it (it hid the audit panel's pagination
 * on the staff details page). Making the bar a flex row instead means the
 * scroll area ends exactly where the bar begins, so nothing can pass beneath
 * it at any scroll position.
 */
export const LayoutShell = styled("div", {
  height: "100vh",
  display: "flex",
  flexDirection: "column",
});

export const ContentContainer = styled("div", {
  width: "100%",
  padding: "62px 0 0 280px",
  // Fills the shell between the top panel and the environment bar.
  // `minHeight: 0` lets it shrink below its content so it, not the page,
  // is what scrolls.
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  backgroundColor: "$gray-20",
  touchAction: "auto",
  transition: "padding 250ms ease",

  "@media screen and (max-width: 1100px)": {
    padding: "62px 0 0 0",
  },

  variants: {
    sidebarState: {
      expanded: {
        padding: "62px 0 0 280px",
        "@media screen and (max-width: 1100px)": {
          padding: "62px 0 0 0",
        },
      },
      collapsed: {
        padding: "62px 0 0 56px",
        "@media screen and (max-width: 1100px)": {
          padding: "62px 0 0 0",
        },
      },
    },
  },
});

/**
 * Full-width bar at the bottom of the /admin viewport so the operator always
 * sees which environment/region the session writes to (EN-5129 UI feedback:
 * bottom placement, solid attention colors, bigger text).
 *
 * The last row of `LayoutShell`, not an overlay — see the note there. Its
 * height is fixed so the sidebar knows where to stop; `alignContent` centres
 * the line inside it while `alignItems: baseline` keeps the title and the
 * sentence sitting on the same baseline.
 */
export const EnvironmentBanner = styled("div", {
  flexShrink: 0,
  height: ENVIRONMENT_BANNER_HEIGHT,
  display: "flex",
  alignItems: "baseline",
  alignContent: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: "8px 16px",
  padding: "12px 24px",

  variants: {
    severity: {
      production: {
        backgroundColor: "$error",
      },
      uat: {
        backgroundColor: "$warning-dark",
      },
      // No blue in the theme tokens — Retool's info blue, kept distinct from
      // the portal's purple $primary so the bar reads "environment notice",
      // not "brand UI".
      default: {
        backgroundColor: "#2556DB",
      },
    },
  },

  defaultVariants: {
    severity: "default",
  },
});

export const EnvironmentBannerTitle = styled("span", {
  fontFamily: "$nunito",
  fontSize: "17px",
  fontWeight: 800,
  letterSpacing: "1px",
  color: "$white",
});

/**
 * Hidden on narrow viewports: below roughly 700px the sentence wraps onto a
 * second line, and the bar has a fixed height. The title carries the part
 * that matters (which environment and region), so it is what survives.
 */
export const EnvironmentBannerText = styled("span", {
  fontFamily: "$openSans",
  fontSize: "14px",
  color: "rgba(255, 255, 255, 0.92)",

  "@media screen and (max-width: 700px)": {
    display: "none",
  },
});

export const NavContentContainer = styled("div", {
  display: "flex",
  flexGrow: 1,

  "@media screen and (max-width: 1100px)": {
    width: "100%",
    justifyContent: "flex-end",
  },
});

export const UnauthorizedContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
  gap: "16px",
  padding: "24px",
  textAlign: "center",
});

export const UnauthorizedTitle = styled("h1", {
  fontFamily: "$nunito",
  fontSize: "$heading-text-h3",
  color: "$black",
  margin: 0,
});

export const UnauthorizedText = styled("p", {
  fontFamily: "$openSans",
  fontSize: "$text-xl",
  color: "$gray-90",
  margin: 0,
  maxWidth: "400px",
});

export const MobileMenuToggle = styled("button", {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});
